import { useCallback, useEffect, useState } from 'react';
import { useProject } from '@/features/project/hooks/use-projects';
import { goalsAutosaveCoordinator } from '@/features/project/lib/goals-autosave-coordinator';
import {
  convertFacadeArea,
  formatFacadeAreaValue,
  parseFacadeArea,
  type FacadeAreaUnit,
} from '@/features/project/ui/FacadeAreaField';

/**
 * Shared goals-editor buffer used by both the standalone goals page and the
 * embedded render-settings goals sheet.
 *
 * Since the goals-dialogue pivot, edits are NOT auto-saved per keystroke — the
 * user commits explicitly (a teal checkmark) so the server-side LLM evaluation
 * runs on a deliberate save, not mid-typing. The buffer still seeds once from
 * the project and never pulls server updates back into the field while editing
 * (the goals-wipe guard, 2026-06-02). `isDirty` drives the commit button and the
 * leave-confirm; after a successful commit the detail query refetches and the
 * server baseline catches up, dropping `isDirty` back to false.
 */
export function useGoalsEditor(projectId: string) {
  const { data: projectResponse, isLoading } = useProject(projectId);
  const project = projectResponse?.data;

  const serverGoals = project?.attributes.goals ?? '';
  const serverRegion = project?.attributes.construction_region ?? '';
  const serverArea =
    project?.attributes.facade_area != null
      ? String(project.attributes.facade_area)
      : '';
  const serverUnit: FacadeAreaUnit =
    project?.attributes.facade_area_unit ?? 'm2';

  const [goals, setGoals] = useState(serverGoals);
  const [region, setRegion] = useState(serverRegion);
  const [area, setArea] = useState(serverArea);
  // Local mutable display unit — the toggle flips it immediately, before the
  // server round-trips. Seeded from the server unit and kept in sync until the
  // user overrides it (same plain-buffer reconcile pattern as the value).
  const [unit, setUnit] = useState<FacadeAreaUnit>(serverUnit);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Only an actually-edited area rides along in the PATCH — sending the field
  // (even unchanged) marks it user-owned and clears the "estimated" flag.
  const areaChanged = area.trim() !== serverArea;
  const unitChanged = unit !== serverUnit;
  const pendingArea = areaChanged ? parseFacadeArea(area) : undefined;

  // Seed once; later server updates are intentionally ignored so a refetch can't
  // trample what the user is typing.
  useEffect(() => {
    if (!project || hasInitialized) return;
    setGoals(serverGoals);
    setRegion(serverRegion);
    setArea(serverArea);
    setUnit(serverUnit);
    setHasInitialized(true);
  }, [project, serverGoals, serverRegion, serverArea, serverUnit, hasInitialized]);

  const isDirty =
    goals.trim() !== serverGoals.trim() ||
    region.trim() !== serverRegion.trim() ||
    areaChanged ||
    unitChanged;

  /**
   * Switch the display unit: convert the current buffer value (if numeric),
   * update the unit, and persist immediately — sending BOTH the converted
   * `facade_area` and the new `facade_area_unit` so the server stores it in the
   * unit the user picked. An empty/invalid buffer still switches the unit
   * (preference persists) without sending a numeric value.
   */
  const changeUnit = useCallback(
    (next: FacadeAreaUnit) => {
      if (next === unit) return;
      const parsed = parseFacadeArea(area);
      let converted: number | null | undefined = parsed;
      let nextArea = area;
      if (typeof parsed === 'number') {
        const value = convertFacadeArea(parsed, unit, next);
        nextArea = formatFacadeAreaValue(value);
        converted = value;
        setArea(nextArea);
      }
      setUnit(next);
      goalsAutosaveCoordinator.flushNow(
        projectId,
        goals,
        region,
        converted,
        next
      );
    },
    [unit, area, projectId, goals, region]
  );

  /** Persist the current buffer (fires the server-side evaluation). */
  const commit = useCallback(() => {
    if (!isDirty) return;
    goalsAutosaveCoordinator.flushNow(
      projectId,
      goals,
      region,
      pendingArea,
      unitChanged ? unit : undefined
    );
  }, [isDirty, projectId, goals, region, pendingArea, unitChanged, unit]);

  /** Drop local edits back to the saved server baseline. */
  const discard = useCallback(() => {
    setGoals(serverGoals);
    setRegion(serverRegion);
    setArea(serverArea);
    setUnit(serverUnit);
  }, [serverGoals, serverRegion, serverArea, serverUnit]);

  return {
    project,
    isLoading,
    hasInitialized,
    goals,
    setGoals,
    region,
    setRegion,
    area,
    setArea,
    unit,
    changeUnit,
    areaChanged,
    isDirty,
    commit,
    discard,
  };
}
